/**
 * Переводит сообщения об ошибках авторизации Supabase на русский язык
 */
export function translateAuthError(errorMessage: string): string {
  const errorMessages: Record<string, string> = {
    // Ошибки входа
    "Invalid login credentials": "Неверный email или пароль",
    "Email not confirmed":
      "Email не подтвержден. Проверьте почту для активации аккаунта",
    "Invalid email or password": "Неверный email или пароль",
    "Email link is invalid or has expired":
      "Ссылка недействительна или срок её действия истек",

    // Ошибки регистрации
    "User already registered": "Пользователь с таким email уже зарегистрирован",
    "Password should be at least 6 characters":
      "Пароль должен содержать не менее 6 символов",
    "Unable to validate email address": "Невозможно проверить email адрес",

    // Ошибки сброса пароля
    "For security purposes, you can only request this once every 60 seconds":
      "В целях безопасности вы можете запрашивать сброс пароля только раз в 60 секунд",
    "No user found with that email": "Пользователь с таким email не найден",

    // Ошибки обновления email
    "A user with this email address has already been registered":
      "Пользователь с таким email адресом уже зарегистрирован",
    email_exists: "Пользователь с таким email адресом уже зарегистрирован",
    "Email update requires reauthentication":
      "Для обновления email требуется повторная аутентификация",
    "Invalid email format": "Неверный формат email адреса",
    "Unable to update email": "Невозможно обновить email адрес",
    "Email confirmation required": "Необходимо подтверждение email адреса",
    "Token has expired or is invalid":
      "Срок действия ссылки истек или она недействительна",
    "Email change token is invalid": "Токен для смены email недействителен",
    "Invalid token": "Недействительный токен",
    "Token expired": "Срок действия токена истек",

    // Ошибки сессии
    "Session expired": "Сессия истекла. Пожалуйста, войдите снова.",
    session_not_found: "Сессия не найдена. Пожалуйста, войдите снова.",
    "Session from session_id claim in JWT does not exist":
      "Сессия истекла. Пожалуйста, войдите снова.",
    "Invalid JWT":
      "Недействительный токен авторизации. Пожалуйста, войдите снова.",
    "JWT expired":
      "Срок действия токена авторизации истек. Пожалуйста, войдите снова.",
    refresh_token_not_found:
      "Токен обновления не найден. Пожалуйста, войдите снова.",
    "Invalid refresh token":
      "Недействительный токен обновления. Пожалуйста, войдите снова.",
    "Refresh token expired":
      "Срок действия токена обновления истек. Пожалуйста, войдите снова.",
    "Unable to refresh token":
      "Невозможно обновить токен. Пожалуйста, войдите снова.",

    // Общие ошибки
    "Server error. Try again later.": "Ошибка сервера. Попробуйте позже.",
    "Request failed with status code 429":
      "Слишком много запросов. Попробуйте позже.",
    "Network error": "Ошибка сети. Проверьте подключение к интернету.",

    // Ошибки обновления пароля
    "New password should be different from the old password":
      "Новый пароль должен отличаться от старого",
    "Password update requires reauthentication":
      "Для обновления пароля требуется повторная аутентификация",
    "User not found": "Пользователь не найден. Пожалуйста, войдите снова.",
    "Authentication required":
      "Требуется аутентификация. Пожалуйста, войдите снова.",
  };

  // Проверяем, есть ли точное соответствие
  if (errorMessage in errorMessages) {
    return errorMessages[errorMessage];
  }

  // Проверяем частичные совпадения
  for (const [key, value] of Object.entries(errorMessages)) {
    if (errorMessage.includes(key)) {
      return value;
    }
  }

  // Если перевод не найден, возвращаем исходное сообщение
  return errorMessage;
}

/**
 * Переводит сообщения об ошибках API на русский язык
 */
export function translateApiError(errorMessage: string): string {
  const apiErrors: Record<string, string> = {
    // Ошибки загрузки документов
    "Имя файла должно содержать расширение": "Выберите файл с расширением .docx, .pdf или .odt",
    "Формат txt не поддерживается": "Этот формат файла не поддерживается",
    "Некорректный BASE64 формат": "Ошибка обработки файла",
    "Ошибка при конвертации файла": "Не удалось конвертировать документ",
    "File size exceeds maximum limit": "Размер файла превышает допустимый лимит",
    "Unsupported file format": "Неподдерживаемый формат файла",

    // Ошибки генерации шаблонов
    "Ошибка генерации шаблона по примеру": "Не удалось сгенерировать промпт",
    "Template generation failed": "Ошибка генерации шаблона",
    "Invalid document description": "Неверное описание документа",
    "Invalid document example": "Неверный пример документа",

    // Общие ошибки API
    "ensure this value has at least 1 characters": "Поле не должно быть пустым",
    "Authentication required": "Требуется авторизация",
    "Invalid token": "Недействительный токен авторизации",
    "Token expired": "Срок действия токена истек",
    "Rate limit exceeded": "Превышен лимит запросов. Попробуйте позже",
    "Internal server error": "Внутренняя ошибка сервера",
    "Service unavailable": "Сервис временно недоступен",

    // Ошибки сети
    "Network Error": "Ошибка сети. Проверьте подключение к интернету",
    "Failed to fetch": "Не удалось выполнить запрос. Проверьте подключение",
    "Request timeout": "Время запроса истекло",
  };

  // Проверяем, есть ли точное соответствие
  if (errorMessage in apiErrors) {
    return apiErrors[errorMessage];
  }

  // Поиск частичных совпадений
  for (const [key, value] of Object.entries(apiErrors)) {
    if (errorMessage.includes(key)) {
      return value;
    }
  }

  // Если не найдено в API ошибках, пробуем ошибки авторизации
  return translateAuthError(errorMessage);
}
